import { useCallback, useMemo, useRef, useState, type FC } from "react";
import type { EditorRef } from "react-email-editor";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import WizardStepper from "src/components/reusable/WizardStepper/WizardStepper";
import type { CampaignDraft } from "src/services/CampaignDraftTypes";
import type { CampaignData } from "../../../../EngagementInterfaces";
import CampaignPageLayout from "../../../shared/CampaignPageLayout";
import CampaignFlowActions from "../../../shared/CampaignFlowActions";
import CampaignNextButton from "../../../shared/CampaignNextButton";
import CampaignDraftDialogs from "../../../shared/CampaignDraftDialogs";
import { CAMPAIGN_ANALYTICS_ROUTE } from "../../../CampaignSetup/CampaignSetupConstants";
import { ShowFieldErrorsContext } from "../../../shared/UseRequiredFieldError";
import EmailStatusPopup from "../AiEmail/EmailStatusPopup";
import ScheduleCampaignModal from "../AiEmail/components/ScheduleCampaignModal";
import CampaignBasicsStep from "../AiEmail/steps/CampaignBasicsStep";
import CreateCampaignStep from "../AiEmail/steps/CreateCampaignStep";
import SenderRecipientsStep from "../AiEmail/steps/SenderRecipientsStep";
import { useCampaignDelivery } from "../AiEmail/hooks/UseCampaignDelivery";
import { useRecommendedProducts } from "../AiEmail/hooks/UseRecommendedProducts";
import { saveEditorDesign } from "../AiEmail/EmailEditorUtils";
import ChooseTemplateStep from "./steps/ChooseTemplateStep";
import TemplatePreviewModal from "./components/TemplatePreviewModal";
import { useEmailTemplates } from "./hooks/UseEmailTemplates";
import { useEmailTemplateWizard } from "./hooks/UseEmailTemplateWizard";
import {
  EMAIL_TEMPLATE_WIZARD_STEPS,
  TEMPLATE_EDITOR_KEY,
  type EmailTemplateStepKey,
} from "./TemplateEditorConstants";
import "../AiEmail/AiCampaignWizard.scss";
import "./TemplateEmailEditor.scss";

const WIZARD_KEY = "campaignFlow.aiWizard";

interface TemplateCampaignWizardProps {
  campaignData: Partial<CampaignData>;
  initialDraft?: CampaignDraft | null;
}

const TemplateCampaignWizard: FC<TemplateCampaignWizardProps> = ({
  campaignData,
  initialDraft = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const editorRef = useRef<EditorRef>(null);
  const templatesState = useEmailTemplates();

  const {
    stepIndex,
    stepKey,
    template,
    preview,
    renderingTemplateId,
    campaignName,
    subjectLine,
    sendTo,
    excludeList,
    audienceOptions,
    excludeOptions,
    companyName,
    isListsLoading,
    nameError,
    senderDetails,
    canProceed,
    updateCampaignName,
    setSubjectLine,
    setSendTo,
    setExcludeList,
    setReplyTo,
    openPreview,
    closePreview,
    applyTemplate,
    goNext,
    goBack,
    goToStep,
    furthestIndex,
    saveTemplateDesign,
    draft,
  } = useEmailTemplateWizard(campaignData, { initialDraft });

  const isEditorStep = stepKey === "editor";
  const recommendedProducts = useRecommendedProducts({
    audienceNames: senderDetails.recipients,
    excludeAudienceNames: senderDetails.exclusions,
    campaignName,
    enabled: isEditorStep,
  });

  const {
    isSending,
    isScheduleOpen,
    deliveryStatus,
    sendNow,
    schedule,
    openSchedule,
    closeSchedule,
    clearDeliveryStatus,
  } = useCampaignDelivery({
    editorRef,
    template,
    campaignName,
    subjectLine,
    senderDetails,
    previousCampaignId: campaignData.originalCampaignId,
    replyTo: senderDetails.replyTo,
    draftId: draft.draftId,
    onDelivered: draft.close,
  });

  const [errorStepKey, setErrorStepKey] = useState<EmailTemplateStepKey | null>(null);
  const [isDraftOpen, setIsDraftOpen] = useState(false);

  const stepperSteps = useMemo(
    () => EMAIL_TEMPLATE_WIZARD_STEPS.map(step => ({ key: step.key, title: t(step.titleKey) })),
    [t]
  );

  const isBusy = Boolean(renderingTemplateId) || isSending;
  const revealStepErrors = useCallback(() => setErrorStepKey(stepKey), [stepKey]);

  const { save: saveDraft } = draft;
  const handleSaveDraft = useCallback(async () => {
    const design = isEditorStep ? await saveEditorDesign(editorRef.current?.editor) : null;
    if (design) {
      saveTemplateDesign(design);
    }
    return saveDraft(design);
  }, [isEditorStep, saveDraft, saveTemplateDesign]);

  const handleBack = useCallback(async () => {
    if (isEditorStep) {
      const design = await saveEditorDesign(editorRef.current?.editor);
      if (design) {
        saveTemplateDesign(design);
      }
    }
    goBack();
  }, [goBack, isEditorStep, saveTemplateDesign]);


  const handleStepSelect = useCallback(
    async (index: number) => {
      if (isEditorStep) {
        const design = await saveEditorDesign(editorRef.current?.editor);
        if (design) {
          saveTemplateDesign(design);
        }
      }
      await goToStep(index);
    },
    [goToStep, isEditorStep, saveTemplateDesign]
  );

  const isStepSelectable = useCallback((index: number) => index <= furthestIndex, [furthestIndex]);

  const renderStep = () => {
    switch (stepKey) {
      case "campaignDetails":
        return (
          <CampaignBasicsStep
            title={t("campaignFlow.createCampaignNew.title")}
            campaignName={campaignName}
            onCampaignNameChange={updateCampaignName}
            companyName={companyName}
            sendTo={sendTo}
            excludeList={excludeList}
            audienceOptions={audienceOptions}
            excludeOptions={excludeOptions}
            isListsLoading={isListsLoading}
            nameError={nameError}
            onSendToChange={setSendTo}
            onExcludeListChange={setExcludeList}
          />
        );
      case "senderRecipients":
        return (
          <SenderRecipientsStep
            subjectLine={subjectLine}
            onSubjectLineChange={setSubjectLine}
            senderDetails={senderDetails}
            sendTo={sendTo}
            audienceOptions={audienceOptions}
            isListsLoading={isListsLoading}
            onReplyToChange={setReplyTo}
            onSendToChange={setSendTo}
          />
        );
      case "chooseTemplate":
        return (
          <ChooseTemplateStep
            templatesState={templatesState}
            brandName={senderDetails.fromName}
            renderingTemplateId={renderingTemplateId}
            onPreview={openPreview}
            onUse={applyTemplate}
          />
        );
      default:
        return (
          <CreateCampaignStep
            title={t(`${TEMPLATE_EDITOR_KEY}.editor.title`)}
            subtitle={t(`${TEMPLATE_EDITOR_KEY}.editor.subtitle`)}
            editorRef={editorRef}
            template={template}
            recommendedProducts={recommendedProducts}
          />
        );
    }
  };

  return (
    <CampaignPageLayout
      stickyChrome
      onExit={() => navigate(CAMPAIGN_ANALYTICS_ROUTE)}
      stepper={
        <WizardStepper
          steps={stepperSteps}
          currentIndex={stepIndex}
          isStepSelectable={isStepSelectable}
          onStepSelect={isBusy ? undefined : handleStepSelect}
        />
      }
      className={renderingTemplateId ? "template-campaign--applying" : undefined}
    >
      <ShowFieldErrorsContext.Provider value={errorStepKey === stepKey}>
        {renderStep()}
      </ShowFieldErrorsContext.Provider>

      <CampaignFlowActions
        sticky
        onBack={handleBack}
        backDisabled={isBusy}
        backLabel={stepIndex === 0 ? t("campaignFlow.common.cancel") : undefined}
      >
        <button
          type="button"
          className="button button--outline campaign-flow__action"
          onClick={() => setIsDraftOpen(true)}
          disabled={isBusy}
        >
          {t("campaignFlow.common.saveDraft")}
        </button>
        {isEditorStep ? (
          <>
            <button
              type="button"
              className="button button--primary campaign-flow__action"
              onClick={openSchedule}
              disabled={isSending}
            >
              {t(`${WIZARD_KEY}.createCampaign.scheduleCampaign`)}
            </button>
            <button
              type="button"
              className="button button--primary campaign-flow__action"
              onClick={sendNow}
              disabled={isSending}
            >
              {isSending
                ? t(`${WIZARD_KEY}.createCampaign.sending`)
                : t(`${WIZARD_KEY}.createCampaign.sendCampaign`)}
            </button>
          </>
        ) : (
          <CampaignNextButton
            canProceed={canProceed && !isBusy}
            onNext={goNext}
            onBlockedClick={revealStepErrors}
          >
            {t(`${WIZARD_KEY}.next`)}
          </CampaignNextButton>
        )}
      </CampaignFlowActions>

      {preview && (
        <TemplatePreviewModal
          templateId={preview.templateId}
          html={preview.html}
          isApplying={renderingTemplateId === preview.templateId}
          onClose={closePreview}
          onUse={applyTemplate}
        />
      )}

      {isScheduleOpen && (
        <ScheduleCampaignModal
          isSubmitting={isSending}
          onCancel={closeSchedule}
          onSchedule={schedule}
        />
      )}

      <CampaignDraftDialogs
        guardActive={!deliveryStatus && (isEditorStep || draft.hasUnsavedChanges)}
        isDraftOpen={isDraftOpen}
        isSaving={draft.isSaving}
        onCloseDraft={() => setIsDraftOpen(false)}
        onSaveDraft={handleSaveDraft}
      />

      {deliveryStatus && (
        <EmailStatusPopup
          type={deliveryStatus.type}
          date={deliveryStatus.date}
          time={deliveryStatus.time}
          onClose={clearDeliveryStatus}
        />
      )}
    </CampaignPageLayout>
  );
};

export default TemplateCampaignWizard;
